import { ErrorState, LazyLoader } from '@bp/ui-components';
import { useDocumentTitle } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { memo, ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { ColorType } from '../../types/global';
import styles from './Page.module.scss';
import { PageHeader } from './PageHeader';

type PageProps = {
  title?: string | null;
  headline?: string | null;
  subheadline?: string | null;
  showSubHeadlineAsChip?: boolean;
  chipTooltip?: string;
  colorHint?: ColorType;
  fullWidth?: boolean;
  fullHeight?: boolean;
  noHeadlineMargin?: boolean;
  hideHeader?: boolean;
  isForbidden: boolean;
  hideTitleIfForbidden?: boolean;
  hasOutlet?: boolean;
  children: ReactNode;
  className?: string | undefined;
};

export const Page = memo(
  ({
    title,
    headline = null,
    subheadline = null,
    showSubHeadlineAsChip = false,
    chipTooltip,
    colorHint,
    fullWidth = false,
    fullHeight = false,
    noHeadlineMargin = false,
    hideHeader = false,
    isForbidden,
    hideTitleIfForbidden,
    hasOutlet = false,
    children,
    className,
  }: PageProps) => {
    const { t } = useTranslation();
    const { pimAuthClaims } = useAuthClaims();
    const organization = pimAuthClaims.getOrganization();

    useDocumentTitle(`${!title ? t('auth.app-title') : title}${organization ? ` - ${organization.name}` : ''}`);

    const classes = classNames(
      styles.page,
      {
        [styles['full-width']]: fullWidth,
        [styles['full-height']]: fullHeight,
        [styles['has-outlet']]: hasOutlet,
      },
      className,
    );

    return isForbidden ? (
      <ErrorState
        settings={{ forcedHeight: '50vh' }}
        type='forbidden'
        pageTitle={!hideTitleIfForbidden ? (title ?? '') : undefined}
      />
    ) : (
      <div className={classes}>
        {!hideHeader && (title || headline) && (
          <PageHeader
            headline={!headline ? title : headline}
            subHeadline={subheadline}
            showSubHeadlineAsChip={showSubHeadlineAsChip}
            chipTooltip={chipTooltip}
            colorHint={colorHint}
            size='l'
            noMargin={noHeadlineMargin}
          />
        )}
        <Suspense fallback={<LazyLoader embedded transparent forceHeight='80vh' size='xxl' />}>{children}</Suspense>
      </div>
    );
  },
);

Page.displayName = 'Page';
